import { useMemo, useState } from 'react';
import { useStaleOrders, useStaleOrdersDates } from '../hooks/useStaleOrders';
import type { StaleOrderRow } from '../types';

const number = (n: number) => n.toLocaleString('en-US');

function formatDate(date: string): string {
  return new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Timestamps arrive as `YYYY-MM-DD HH:MM:SS PDT|PST`; JS can't parse the zone
// abbreviation, so day-level age is computed from the date part alone.
function daysSince(pacificTs: string, refMs: number): number | null {
  const day = pacificTs.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  return Math.max(0, Math.floor((refMs - new Date(day + 'T00:00:00').getTime()) / DAY_MS));
}

interface ReasonMeta {
  short: string;
  badgeCls: string;
  explanation: string;
  actionRequired: boolean;
}

const REASON_META: Record<string, ReasonMeta> = {
  'OMS Status Sync Issue': {
    short: 'OMS Sync Issue',
    badgeCls: 'bg-purple-100 text-purple-800',
    explanation: 'Order status wrong despite shipped memos — create OMS ticket',
    actionRequired: true,
  },
  '3PL Processing Delay': {
    short: '3PL Delay',
    badgeCls: 'bg-blue-100 text-blue-800',
    explanation: 'Ship memo still unshipped — follow up with 3PL',
    actionRequired: true,
  },
  'Manual Review Required': {
    short: 'Manual Review',
    badgeCls: 'bg-amber-100 text-amber-800',
    explanation: 'Investigate processing issue or stuck rerouting',
    actionRequired: true,
  },
  'Presale Order': {
    short: 'Presale',
    badgeCls: 'bg-gray-100 text-gray-600',
    explanation: 'Waiting for inventory — no action needed',
    actionRequired: false,
  },
};

const REASON_ORDER = [
  'OMS Status Sync Issue',
  '3PL Processing Delay',
  'Manual Review Required',
  'Presale Order',
];

const UNKNOWN_REASON_META: ReasonMeta = {
  short: 'Unknown',
  badgeCls: 'bg-gray-100 text-gray-600',
  explanation: 'Unrecognized stale reason from upstream',
  actionRequired: false,
};

const reasonMeta = (reason: string): ReasonMeta => REASON_META[reason] ?? UNKNOWN_REASON_META;

function ReasonBadge({ reason }: { reason: string }) {
  const meta = reasonMeta(reason);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${meta.badgeCls}`}
      title={meta.explanation}
    >
      {meta.short}
    </span>
  );
}

function WarehouseCell({ row }: { row: StaleOrderRow }) {
  if (row.shipMemoInAirboss === null) {
    return <span className="text-gray-300">—</span>;
  }
  if (row.shipMemoInAirboss === false) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
        title="This ship memo has no fulfillment_shipmemo row in AirBoss"
      >
        Not in AirBoss
      </span>
    );
  }
  return (
    <div>
      <div className="text-sm text-gray-700 whitespace-nowrap">
        {row.shipMemoWarehouse ?? '—'}
        {row.shipMemoWarehouseStatus && (
          <span
            className={
              row.shipMemoWarehouseStatus === 'pending_warehouse_update'
                ? 'ml-1.5 text-amber-600'
                : 'ml-1.5 text-gray-500'
            }
          >
            · {row.shipMemoWarehouseStatus}
          </span>
        )}
      </div>
      {row.warehouseLastStatusReceivedAt && (
        <div className="text-xs text-gray-400 whitespace-nowrap">
          last update {row.warehouseLastStatusReceivedAt}
        </div>
      )}
    </div>
  );
}

const INITIAL_ROWS = 50;

export function StaleOrders() {
  const { data: dates, isLoading: datesLoading } = useStaleOrdersDates();
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const effectiveDate = selectedDate ?? dates?.[0]?.date;

  const { data: report, isLoading, error } = useStaleOrders(effectiveDate);

  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);
  const [showAllRows, setShowAllRows] = useState(false);

  // Age is measured against the snapshot's generation time so historical days stay accurate.
  const refMs = useMemo(
    () => (report?.generatedAt ? new Date(report.generatedAt).getTime() : Date.now()),
    [report?.generatedAt]
  );

  const reasonCounts = report?.summary.byReason ?? {};
  const knownReasons = REASON_ORDER.filter((r) => reasonCounts[r] !== undefined);
  const extraReasons = Object.keys(reasonCounts).filter((r) => !REASON_ORDER.includes(r));
  const cardReasons = [...knownReasons, ...extraReasons];

  const visibleRows = useMemo(() => {
    let rows = report?.rows ?? [];
    if (reasonFilter) {
      rows = rows.filter((r) => r.staleReason === reasonFilter);
    }
    const s = search.trim().toLowerCase();
    if (s) {
      rows = rows.filter(
        (r) =>
          r.shopifyOrderId.toLowerCase().includes(s) ||
          r.shopifyOrderName.toLowerCase().includes(s) ||
          r.smNumbers.toLowerCase().includes(s)
      );
    }
    // Stalest first (oldest OMS modification)
    return [...rows].sort((a, b) => a.omsLastModifiedAt.localeCompare(b.omsLastModifiedAt));
  }, [report, reasonFilter, search]);

  const displayedRows = showAllRows ? visibleRows : visibleRows.slice(0, INITIAL_ROWS);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stale OMS Orders</h1>
          <p className="mt-1 text-sm text-gray-500">
            Daily snapshot of OMS orders untouched for 7+ days (created within 60 days, not
            completed/cancelled). Read-only — informational for now.
            {report?.generatedAt && (
              <span className="text-gray-400">
                {' '}Generated {new Date(report.generatedAt).toLocaleString('en-US')}.
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="stale-date" className="text-sm text-gray-500">
            Date
          </label>
          <select
            id="stale-date"
            value={effectiveDate ?? ''}
            onChange={(e) => setSelectedDate(e.target.value)}
            disabled={datesLoading || !dates || dates.length === 0}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
          >
            {dates?.map((d) => (
              <option key={d.date} value={d.date}>
                {formatDate(d.date)} ({d.rowCount})
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-red-600">
            Error loading stale orders: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {!error && !datesLoading && dates && dates.length === 0 && (
        <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-900">No reports available</h3>
          <p className="mt-1 text-sm text-gray-500">
            No stale-orders reports were found for the last 30 days.
          </p>
        </div>
      )}

      {(isLoading || datesLoading) && (
        <div className="space-y-4 animate-pulse">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <div className="h-4 bg-gray-200 rounded w-24" />
                <div className="mt-3 h-7 bg-gray-200 rounded w-16" />
              </div>
            ))}
          </div>
          <div className="h-64 bg-white rounded-lg border border-gray-200" />
        </div>
      )}

      {!isLoading && !error && report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <button
              type="button"
              onClick={() => setReasonFilter(null)}
              className={`bg-white rounded-lg border shadow-sm p-4 text-left ${
                reasonFilter === null ? 'border-blue-400 ring-1 ring-blue-400' : 'border-gray-200'
              }`}
            >
              <p className="text-sm text-gray-500">Total stale orders</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {number(report.summary.orderCount)}
              </p>
              <p className="mt-1 text-xs text-gray-400 truncate">
                {number(report.summary.rowCount)} rows in snapshot
              </p>
            </button>
            {cardReasons.map((reason) => {
              const meta = reasonMeta(reason);
              const active = reasonFilter === reason;
              return (
                <button
                  key={reason}
                  type="button"
                  onClick={() => setReasonFilter(active ? null : reason)}
                  className={`bg-white rounded-lg border shadow-sm p-4 text-left ${
                    active ? 'border-blue-400 ring-1 ring-blue-400' : 'border-gray-200'
                  }`}
                  title={meta.explanation}
                >
                  <p className="text-sm text-gray-500 truncate">{reason}</p>
                  <p
                    className={`mt-1 text-2xl font-semibold ${
                      meta.actionRequired ? 'text-amber-600' : 'text-gray-900'
                    }`}
                  >
                    {number(reasonCounts[reason] ?? 0)}
                  </p>
                  <p className="mt-1 text-xs text-gray-400 truncate">{meta.explanation}</p>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order id, order name, or ship memo…"
              className="w-full sm:w-96 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {reasonFilter && (
              <button
                type="button"
                onClick={() => setReasonFilter(null)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Clear filter: {reasonFilter} ×
              </button>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OMS Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ship Memos</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stale For</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {displayedRows.map((row, idx) => {
                    const createdDays = daysSince(row.shopifyCreatedAt, refMs);
                    const staleDays = daysSince(row.omsLastModifiedAt, refMs);
                    return (
                      <tr key={`${row.shopifyOrderId}-${row.smNumbers}-${idx}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <a
                            href={`https://admin.shopify.com/store/skimsbody/orders/${row.shopifyOrderId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 hover:text-blue-700"
                          >
                            {row.shopifyOrderName || row.shopifyOrderId}
                          </a>
                          <div className="text-xs text-gray-400">{row.shopifyOrderId}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <ReasonBadge reason={row.staleReason} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-700">{row.omsOrderStatus || '—'}</div>
                          {row.shopifyFinancialStatus && (
                            <div className="text-xs text-gray-400">{row.shopifyFinancialStatus}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div
                            className="text-sm text-gray-700 max-w-[16rem] truncate"
                            title={row.smNumbers}
                          >
                            {row.smNumbers || '—'}
                          </div>
                          {row.shipMemoStatuses && row.shipMemoStatuses !== 'No ship memos' && (
                            <div
                              className="text-xs text-gray-400 max-w-[16rem] truncate"
                              title={row.shipMemoStatuses}
                            >
                              {row.shipMemoStatuses}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <WarehouseCell row={row} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-700">{row.shopifyCreatedAt.slice(0, 10)}</div>
                          {createdDays !== null && (
                            <div className="text-xs text-gray-400">{number(createdDays)}d ago</div>
                          )}
                        </td>
                        <td
                          className="px-4 py-3 whitespace-nowrap text-sm text-gray-700"
                          title={`Last OMS modification: ${row.omsLastModifiedAt}`}
                        >
                          {staleDays !== null ? `${number(staleDays)}d` : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {row.shipToCountry || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {visibleRows.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">No orders match the current filters.</p>
              </div>
            )}

            {visibleRows.length > 0 && (
              <div className="text-center py-3 border-t border-gray-100">
                <span className="text-sm text-gray-500">
                  Showing {number(displayedRows.length)} of {number(visibleRows.length)} rows
                </span>
                {visibleRows.length > INITIAL_ROWS && (
                  <button
                    type="button"
                    onClick={() => setShowAllRows((v) => !v)}
                    className="ml-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    {showAllRows ? 'Show less' : 'Show all'}
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
